import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voice } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { StatusPill, type PillTone } from '../../../components/panel';
import { formatDateTime } from '../../../lib/format';
import { formatCredits } from '../../../lib/credits';

const MUTED = 'var(--rr-color-text-muted)';
const PAGE = 25;

/** "1 min", "2 min 30 s": the time a call was charged for. */
function length(seconds: number, t: Translate): string {
  if (seconds <= 0) return t('voice.calls.length.none');
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return s === 0 ? t('voice.calls.length.minutes', { minutes: m }) : t('voice.calls.length.minutesSeconds', { minutes: m, seconds: s });
}

const endTone = (c: voice.VoiceCall): PillTone =>
  c.status !== 'ended' ? 'active'
    : c.end_reason === 'completed' || c.end_reason === 'caller_hung_up' ? 'neutral'
      : c.end_reason === 'failed' || c.end_reason === 'engine_lost' ? 'rejected' : 'paused';

/**
 * The organisation's calls: who called which number, how long it was charged
 * for, what it cost, how it ended, and what was said.
 */
export function Calls() {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(() => new URLSearchParams(window.location.search).get('call'));
  const list = useQuery({ queryKey: ['voice-calls', offset], queryFn: () => voice.listCalls({ limit: PAGE, offset }), refetchInterval: 15_000 });
  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;

  return (
    <Card withBorder data-testid="voice-calls">
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={4}>{t('voice.calls.title')}</Title>
          <Text role="bodySmall" c={MUTED}>{t('voice.calls.intro')}</Text>
        </Stack>
        {list.error && <Alert color="error" role="alert">{(list.error as Error).message}</Alert>}
        {list.isPending && <Skeleton height={120} />}
        {!list.isPending && rows.length === 0 && <EmptyState title={t('voice.calls.empty')} description={t('voice.calls.emptyHint')} />}
        {rows.map((c) => (
          <button key={c.id} type="button" className="rr-voice__row rr-voice__row--button" data-testid="voice-call" onClick={() => setOpen(c.id)}>
            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
              <Group gap="xs" wrap="wrap">
                <Text role="bodySmall" fw={650}>{c.from ?? t('voice.calls.unknownCaller')}</Text>
                <StatusPill tone={endTone(c)}>{t(c.status === 'ended' ? `voice.calls.ended.${c.end_reason ?? 'completed'}` : `voice.calls.status.${c.status}`)}</StatusPill>
                {c.channel === 'whatsapp' && <StatusPill tone="neutral">{t('voice.calls.whatsapp')}</StatusPill>}
                {c.quality && c.quality !== 'good' && <StatusPill tone={c.quality === 'poor' ? 'rejected' : 'paused'}>{t(`voice.calls.quality.${c.quality}`)}</StatusPill>}
                {c.recording === 'stored' && <StatusPill tone="neutral">{t('voice.calls.recorded')}</StatusPill>}
              </Group>
              <Text role="caption" c={MUTED}>{t('voice.calls.line', { when: formatDateTime(c.started_at), number: c.to ?? '' })}</Text>
              {c.transferred_to && <Text role="caption" c={MUTED}>{t('voice.calls.passedTo', { who: c.transferred_to })}</Text>}
              {c.summary && <Text role="caption">{c.summary}</Text>}
            </Stack>
            <Stack gap={2} align="flex-end">
              <Text role="bodySmall" fw={600}>{c.charged_minor > 0 ? formatCredits(c.charged_minor, t) : t('voice.calls.noCharge')}</Text>
              <Text role="caption" c={MUTED}>{length(c.billed_seconds, t)}</Text>
            </Stack>
          </button>
        ))}
        {total > PAGE && (
          <Group justify="space-between">
            <Text role="caption" c={MUTED}>{t('voice.calls.showing', { from: offset + 1, to: Math.min(offset + PAGE, total), total })}</Text>
            <Group gap="xs">
              <Button size="xs" variant="default" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>{t('voice.calls.newer')}</Button>
              <Button size="xs" variant="default" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>{t('voice.calls.older')}</Button>
            </Group>
          </Group>
        )}
      </Stack>
      <CallDetail id={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

function CallDetail({ id, onClose }: { id: string | null; onClose: () => void }) {
  const t = useT();
  const q = useQuery({ queryKey: ['voice-call', id], queryFn: () => voice.getCall(id!), enabled: id !== null });
  const c = q.data?.data;
  // What was said, without the housekeeping lines unless they say something a person needs.
  const lines = (c?.transcript ?? []).filter((e) => e.text.trim().length > 0);

  return (
    <Drawer opened={id !== null} onClose={onClose} title={t('voice.calls.detail.title')} position="right" size="lg">
      {q.isPending && <Skeleton height={200} />}
      {q.error && <Alert color="error" role="alert">{(q.error as Error).message}</Alert>}
      {c && (
        <Stack gap="md" data-testid="voice-call-detail">
          <dl className="rr-voice__facts">
            <dt>{t('voice.calls.detail.from')}</dt><dd>{c.from ?? t('voice.calls.unknownCaller')}</dd>
            <dt>{t('voice.calls.detail.to')}</dt><dd>{c.to ?? ''}</dd>
            <dt>{t('voice.calls.detail.when')}</dt><dd>{formatDateTime(c.started_at)}</dd>
            <dt>{t('voice.calls.detail.ended')}</dt><dd>{t(c.status === 'ended' ? `voice.calls.ended.${c.end_reason ?? 'completed'}` : `voice.calls.status.${c.status}`)}</dd>
            <dt>{t('voice.calls.detail.length')}</dt><dd>{length(c.billed_seconds, t)}</dd>
            <dt>{t('voice.calls.detail.cost')}</dt><dd>{c.charged_minor > 0 ? formatCredits(c.charged_minor, t) : t('voice.calls.noCharge')}</dd>
            {c.quality && <><dt>{t('voice.calls.detail.quality')}</dt><dd>{t(`voice.calls.quality.${c.quality}`)}</dd></>}
          </dl>
          {c.recording && <Recording call={c} />}
          {c.transferred_to && <Text role="bodySmall">{t('voice.calls.passedTo', { who: c.transferred_to })}</Text>}
          {c.status !== 'ended' && !c.transferred_to && c.direction === 'inbound' && <PassOn call={c} />}
          <Text role="caption" c={MUTED}>{t('voice.calls.detail.costHint')}</Text>
          {c.summary && <Alert color="info">{c.summary}</Alert>}
          <Stack gap="xs">
            <Title order={5}>{t('voice.calls.detail.transcript')}</Title>
            {lines.length === 0 && <Text role="bodySmall" c={MUTED}>{t('voice.calls.detail.noTranscript')}</Text>}
            <div className="rr-voice__transcript">
              {lines.map((e, i) => (
                <div key={i} className={`rr-voice__line rr-voice__line--${e.kind}`}>
                  <Text role="caption" c={MUTED}>{t(`voice.calls.speaker.${e.kind}`)}</Text>
                  <Text role="bodySmall">{e.text}</Text>
                </div>
              ))}
            </div>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}

/** A call in progress with the assistant can be passed to a person from here. */
function PassOn({ call }: { call: voice.VoiceCall }) {
  const t = useT();
  const qc = useQueryClient();
  const lines = useQuery({ queryKey: ['voice-extensions'], queryFn: () => voice.listExtensions() });
  const groups = useQuery({ queryKey: ['voice-groups'], queryFn: () => voice.listGroups() });
  const [target, setTarget] = useState('');
  const options = [
    ...(groups.data?.data ?? []).filter((g) => g.is_active && g.extension_ids.length > 0).map((g) => ({ value: `g:${g.id}`, label: g.name })),
    ...(lines.data?.data ?? []).filter((x) => x.is_active && (x.kind === 'forward' || x.signed_in)).map((x) => ({ value: `x:${x.id}`, label: `${x.name} (${x.number})` }))
  ];
  const pass = useMutation({
    mutationFn: () => voice.transferCall(call.id, target.startsWith('g:') ? { groupId: target.slice(2) } : { extensionId: target.slice(2) }),
    onSuccess: (res) => {
      notifySuccess(t('voice.calls.pass.done', { who: res.data.to }));
      void qc.invalidateQueries({ queryKey: ['voice-call', call.id] });
      void qc.invalidateQueries({ queryKey: ['voice-calls'] });
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  });
  if (options.length === 0) return null;
  return (
    <Group align="flex-end" gap="sm" data-testid="voice-pass-on">
      <div style={{ flex: 1, minWidth: 0 }}>
        <Select
          label={t('voice.calls.pass.label')} description={t('voice.calls.pass.hint')}
          value={target} onChange={(e) => setTarget(e.currentTarget.value)}
          data={[{ value: '', label: t('voice.calls.pass.choose') }, ...options]}
        />
      </div>
      <Button loading={pass.isPending} disabled={!target} onClick={() => pass.mutate()}>{t('voice.calls.pass.go')}</Button>
    </Group>
  );
}

/** The call's recording: a player once it is stored, and a plain sentence for every other state. */
function Recording({ call }: { call: voice.VoiceCall }) {
  const t = useT();
  const [listen, setListen] = useState<voice.VoiceRecording | null>(null);
  const open = useMutation({
    mutationFn: () => voice.getCallRecording(call.id),
    onSuccess: (res) => setListen(res.data),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'))
  });
  if (call.recording !== 'stored') return <Text role="bodySmall" c={MUTED} data-testid="voice-recording-state">{t(`voice.calls.recording.${call.recording}`)}</Text>;
  return (
    <Stack gap="xs" data-testid="voice-recording">
      {listen ? (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below is the caption */}
          <audio controls src={listen.url} className="rr-voice__audio" />
          {listen.expires_at && <Text role="caption" c={MUTED}>{t('voice.calls.recording.keptUntil', { when: formatDateTime(listen.expires_at) })}</Text>}
        </>
      ) : (
        <Group gap="sm" align="center">
          <Button size="xs" variant="default" loading={open.isPending} onClick={() => open.mutate()}>{t('voice.calls.recording.listen')}</Button>
          <Text role="caption" c={MUTED}>{t('voice.calls.recording.listenHint')}</Text>
        </Group>
      )}
    </Stack>
  );
}
